/*
  Dynamic "Download as PDF" for the portfolio section.

  This file deliberately lives in assets/ rather than in an inline script tag
  inside sections/portfolio.liquid. Shopify's app-embed injector rewrites the
  rendered HTML document and matches body tags with an attribute-tolerant
  pattern. The HTML strings below (used to seed the hidden iframe) were being
  matched inside their own JavaScript string literals, and an app block --
  including its own closing script tag -- was injected into the middle of the
  line. That terminated the inline script thousands of characters early,
  leaving an unterminated string and an unclosed IIFE, so nothing executed and
  the delegated click listener was never registered.

  A theme asset is served as a separate application/javascript response and is
  never HTML-parsed or rewritten, so the markup strings here are out of reach
  of that injector.

  The three values that must come from Liquid (the two pinned library URLs and
  the artist name used for the filename) are passed in on the script tag as
  data attributes instead of being interpolated into the source.
*/
(function () {
  var PDF_SCALE_DESKTOP = 2.4;
  var PDF_SCALE_MOBILE = 1.5;
  var JPEG_QUALITY = 0.85;
  var MOBILE_BREAKPOINT = 768;

  var cfgEl = document.getElementById('pf-pdf-script');
  if (!cfgEl) return;
  var CFG = {
    html2canvasUrl: cfgEl.getAttribute('data-h2c') || '',
    jspdfUrl: cfgEl.getAttribute('data-jspdf') || '',
    artistName: cfgEl.getAttribute('data-artist') || ''
  };

  var generating = false;
  var isGerman = ((document.documentElement.lang || '').toLowerCase().split('-')[0] === 'de');
  var libsPromise = null;
  var iframeEl = null;

  /*
    Buttons are re-queried live (never cached in a module-level NodeList) and
    the click listener is delegated to document rather than bound to each
    button, so the handler survives any re-render or replacement of the button
    nodes themselves.
  */
  function allBtns() {
    return document.querySelectorAll('[data-pf-pdf-btn]');
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[data-pf-lib="' + src + '"]')) { resolve(); return; }
      var s = document.createElement('script');
      s.src = src;
      s.setAttribute('data-pf-lib', src);
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }

  function ensureLibs() {
    if (!libsPromise) {
      libsPromise = Promise.all([
        loadScript(CFG.html2canvasUrl),
        loadScript(CFG.jspdfUrl)
      ]);
    }
    return libsPromise;
  }

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  function setAllBtnState(label) {
    allBtns().forEach(function (b) {
      b.textContent = label;
      b.disabled = true;
    });
  }

  function resetAllBtns() {
    allBtns().forEach(function (b) {
      b.textContent = b.getAttribute('data-pf-pdf-default') || 'Download as PDF';
      b.disabled = false;
    });
  }

  function getIframe() {
    if (iframeEl && iframeEl.isConnected) return Promise.resolve(iframeEl);
    var f = document.createElement('iframe');
    f.setAttribute('aria-hidden', 'true');
    f.style.cssText = 'position:fixed;top:0;left:-99999px;width:794px;height:1123px;border:0;visibility:hidden;';
    document.body.appendChild(f);
    iframeEl = f;
    return new Promise(function (resolve) {
      f.addEventListener('load', function () { resolve(f); }, { once: true });
      f.srcdoc = '<!doctype html><html><head></head><body></body></html>';
    });
  }

  function waitForReady(doc) {
    var imgs = Array.prototype.slice.call(doc.images);
    var imgPromises = imgs.map(function (img) {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise(function (resolve) {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    });
    var fontsReady = (doc.fonts && doc.fonts.ready) ? doc.fonts.ready : Promise.resolve();
    return Promise.all([fontsReady, Promise.all(imgPromises)]).then(function () {
      return new Promise(function (resolve) { setTimeout(resolve, 400); });
    });
  }

  function populateIframe(frame) {
    var printNode = document.querySelector('.print-portfolio');
    if (!printNode) return Promise.reject(new Error('print-portfolio not found'));

    var doc = frame.contentDocument;
    doc.open();
    doc.write(
      '<!doctype html><html><head><meta charset="utf-8">' +
      '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap">' +
      '</head><body style="margin:0;padding:0;background:#fff;"></body></html>'
    );
    doc.close();

    var styleSrc = document.getElementById('pf-print');
    if (styleSrc) {
      var styleClone = doc.createElement('style');
      styleClone.textContent = styleSrc.textContent + '\n.print-portfolio{display:block !important;}';
      doc.head.appendChild(styleClone);
    }

    doc.body.innerHTML = '';
    var clone = printNode.cloneNode(true);
    clone.removeAttribute('aria-hidden');
    doc.body.appendChild(clone);

    return waitForReady(doc);
  }

  function generate() {
    if (generating) return;
    generating = true;
    setAllBtnState(isGerman ? 'PDF wird vorbereitet…' : 'Preparing…');

    var scale = isMobile() ? PDF_SCALE_MOBILE : PDF_SCALE_DESKTOP;

    ensureLibs()
      .then(getIframe)
      .then(populateIframe)
      .then(function () {
        var doc = iframeEl.contentDocument;
        var pages = doc.querySelectorAll('.print-page');
        if (!pages.length) throw new Error('No print pages found in iframe');

        var jsPDFCtor = window.jspdf && window.jspdf.jsPDF;
        if (!window.html2canvas || !jsPDFCtor) throw new Error('PDF libraries failed to load');

        var pdf = new jsPDFCtor({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
        var total = pages.length;
        var chain = Promise.resolve();

        pages.forEach(function (pageEl, i) {
          chain = chain.then(function () {
            setAllBtnState((isGerman ? 'PDF wird vorbereitet ' : 'Preparing ') + (i + 1) + ' / ' + total);
            return window.html2canvas(pageEl, {
              scale: scale,
              useCORS: true,
              backgroundColor: '#ffffff',
              logging: false
            }).then(function (canvas) {
              var imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
              if (i > 0) pdf.addPage('a4', 'portrait');
              pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
              canvas.width = 0;
              canvas.height = 0;
            });
          });
        });

        return chain.then(function () { return pdf; });
      })
      .then(function (pdf) {
        var rawName = CFG.artistName;
        var name = (rawName || 'Portfolio').toString().trim().replace(/\s+/g, '-') || 'Portfolio';
        pdf.save(name + '-Portfolio.pdf');
        resetAllBtns();
        generating = false;
      })
      .catch(function (err) {
        console.error('PDF generation failed, falling back to browser print dialog:', err);
        resetAllBtns();
        generating = false;
        window.print();
      });
  }

  document.addEventListener('click', function (e) {
    var target = e.target && e.target.closest ? e.target.closest('[data-pf-pdf-btn]') : null;
    if (!target) return;
    generate();
  });
})();
